import { CrownIcon, MedalIcon } from "lucide-react";
import { cn } from "@util/tailwind";

interface Props {
    rank: number;
    name: string;
    points: number;
    avatar?: string;
    /** When true, render the highlighted "you" treatment. */
    isCurrentUser?: boolean;
}

const RANK_COLORS: Record<number, string> = {
    1: "bg-amber-400 text-white",
    2: "bg-neutral-400 text-white",
    3: "bg-amber-700 text-white",
};

function RankBadge({ rank }: { rank: number }) {
    const isPodium = rank >= 1 && rank <= 3;
    if (isPodium) {
        const Icon = rank === 1 ? CrownIcon : MedalIcon;
        return (
            <div
                className={cn(
                    "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0",
                    RANK_COLORS[rank]
                )}
                aria-label={`Rank ${rank}`}
            >
                <Icon className="w-4 h-4" />
            </div>
        );
    }
    return (
        <div
            className="w-8 h-8 rounded-full bg-neutral-100 text-neutral-600 flex items-center justify-center flex-shrink-0 text-xs font-semibold tabular-nums"
            aria-label={`Rank ${rank}`}
        >
            {rank}
        </div>
    );
}

export default function LeaderboardItem({
    rank,
    name,
    points,
    avatar,
    isCurrentUser = false,
}: Props) {
    const totalPoints = points;
    const hasPoints = points > 0;

    return (
        <div
            className={cn(
                "flex items-center justify-between gap-3 py-2.5 px-3 rounded-lg transition-colors",
                isCurrentUser
                    ? "bg-blue-50 border border-blue-200"
                    : "border border-transparent hover:bg-neutral-50"
            )}
        >
            <div className="flex items-center gap-3 min-w-0">
                <RankBadge rank={rank} />

                <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 bg-neutral-100">
                    <img
                        onError={(e) => {
                            e.currentTarget.src = `${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/commons/default_avatar.png`;
                        }}
                        src={`${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/${avatar}`}
                        className="w-full h-full object-cover"
                        alt=""
                    />
                </div>

                <div className="flex items-center gap-2 min-w-0">
                    <span
                        className={cn(
                            "text-sm font-medium truncate",
                            isCurrentUser ? "text-blue-900" : "text-gray-900"
                        )}
                    >
                        {name}
                    </span>
                    {isCurrentUser && (
                        <span className="text-[10px] uppercase tracking-wider font-semibold text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded-full flex-shrink-0">
                            You
                        </span>
                    )}
                </div>
            </div>

            <div
                className={cn(
                    "flex items-baseline gap-1 flex-shrink-0 text-sm tabular-nums",
                    hasPoints
                        ? isCurrentUser
                            ? "text-blue-900 font-semibold"
                            : "text-gray-900 font-semibold"
                        : "text-neutral-400"
                )}
            >
                <span>{totalPoints}</span>
                <span className="text-xs font-normal text-neutral-500">
                    {totalPoints === 1 ? "pt" : "pts"}
                </span>
            </div>
        </div>
    );
}
